import { DateInput } from '@/common/components/DateInput/DateInput'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { dateTransformer } from '@/common/utils/dateTransformer'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useAnticipateGovernmentAllocation } from '@/features/privateCompanies/hooks/useAnticipateGovernmentAllocation'
import { GovernmentAllocationInfoItem } from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/Show/components/GovernmentAllocationInfoItem'
import {
  anticipateGovernmentAllocationSchema,
  AnticipateGovernmentAllocationSchema,
} from '@/features/privateCompanies/schemas/anticipateGovernmentAllocation.schema'
import { CompanyGovernmentAllocationData } from '@/features/privateCompanies/types/companyGovernmentAllocation.type'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { InfoIcon } from 'lucide-react'
import { Dispatch, SetStateAction, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

interface AnticipateGovernmentAllocationModalProps {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
  companyGovernmentAllocation: CompanyGovernmentAllocationData
}

export const AnticipateGovernmentAllocationModal = ({
  isOpen,
  setIsOpen,
  companyGovernmentAllocation,
}: AnticipateGovernmentAllocationModalProps) => {
  const { companyId, id: governmentAllocationId } = useParams()
  const queryClient = useQueryClient()

  const { getUserPermission } = useUserPermissions()

  const permittedBypassLimitValidation =
    getUserPermission('Admin::Billing::GovernmentAllocations::Anticipate', true)?.properties
      ?.permitted_bypass_limit_validation === true

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    control,
    handleSubmit,
    setError,
    reset: resetForm,
    formState: { errors },
  } = useForm<AnticipateGovernmentAllocationSchema>({
    resolver: zodResolver(anticipateGovernmentAllocationSchema),
    defaultValues: {
      requested_new_start_date: undefined,
    },
  })

  const onSubmit: SubmitHandler<AnticipateGovernmentAllocationSchema> = (data) => {
    anticipateGovernmentAllocation(data)
  }

  const onSuccess = () => {
    resetForm()
    queryClient.invalidateQueries({ queryKey: ['companyGovernmentAllocations', companyId] })
    queryClient.invalidateQueries({ queryKey: ['companyGovernmentAllocation', governmentAllocationId] })
    setIsOpen(false)
  }

  const {
    mutate: anticipateGovernmentAllocation,
    isPending: anticipateGovernmentAllocationIsPending,
    isError: anticipateGovernmentAllocationIsError,
  } = useAnticipateGovernmentAllocation({
    governmentAllocationId,
    onSuccess,
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error)
    },
  })

  return (
    <>
      <Modal
        isOpen={isOpen && !messages}
        setIsOpen={(value) => {
          if (!value) {
            setTimeout(() => resetForm({ requested_new_start_date: undefined }), 300)
          }

          setIsOpen(value)
        }}
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonIsLoading={anticipateGovernmentAllocationIsPending}
        disabled={anticipateGovernmentAllocationIsPending}
        title="Antecipar período do empenho"
        footer={`${anticipateGovernmentAllocationIsPending ? 'Antecipando' : 'Antecipar'} período de empenho`}
        body={
          <div className="flex flex-col gap-y-4 text-white-800">
            <div className="flex gap-x-2 items-center">
              <InfoIcon strokeWidth={1} className="text-warning" />

              <div>
                <p className="font-bold text-[0.625rem] leading-3">
                  {permittedBypassLimitValidation
                    ? 'Você tem permissão para antecipar o período para qualquer data deste ano.'
                    : 'Você pode antecipar o período do empenho em até 25% do período atual.'}
                </p>
              </div>
            </div>

            <GovernmentAllocationInfoItem
              label="Período de competência"
              value={`${dateTransformer(companyGovernmentAllocation.applicable_start_date)} até ${dateTransformer(
                companyGovernmentAllocation.applicable_end_date,
              )}`}
            />

            <form className="flex flex-col">
              <DateInput
                fieldName="requested_new_start_date"
                control={control}
                errors={errors}
                label="Nova data inicial do período de competência"
                markAsRequired
                disabled={anticipateGovernmentAllocationIsPending}
              />
            </form>
          </div>
        }
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={anticipateGovernmentAllocationIsError}
        />
      )}
    </>
  )
}
